import { Box, Button, Icon, Text } from "@modernatx/ui-kit-react-v2";
import { useRouter } from "next/router";
import React from "react";

import { useExperience } from "@/context/ExperienceContext";
import { FinderBlockProps } from "@/types/Block";
import { FinderLocation } from "@/types/FinderLocation";

import { FinderCheckboxProps } from "../FinderHeader";
import { useFinder } from "../hooks/useFinderV2";
import { customerWebsite } from "../utils";
import { LoadMoreButton } from "./LoadMoreButton";

const LocationTitle = ({
  location
}: {
  distanceTemplate: string;
  location: FinderLocation;
}): React.ReactNode => {
  const { streetAddress, city, stateProvince } = location.location;
  const { name, phoneNumber } = location.customer;

  const { country } = useExperience();

  return (
    <Box>
      <Text variant="h5" sx={{ color: "text-emphasis" }}>
        {name}
      </Text>
      {streetAddress && (
        <Text variant="body-1-regular">
          {country === "jp" ? city + streetAddress : `${streetAddress}, ${city}, ${stateProvince}`}
        </Text>
      )}
      {phoneNumber && (
        <Text variant="body-1-regular">
          <br />
          {phoneNumber}
        </Text>
      )}
    </Box>
  );
};

export const ProductBanner = ({
  productOptions,
  productsAtLocation
}: {
  productOptions: FinderCheckboxProps[];
  productsAtLocation: string[];
}) => {
  const { vaccineTypesFromQuery } = useFinder();

  const showCovid =
    vaccineTypesFromQuery.length === 0 ||
    vaccineTypesFromQuery.includes("covid-19") ||
    vaccineTypesFromQuery.includes("covid-19&rsv");
  const showRsv =
    vaccineTypesFromQuery.length === 0 ||
    vaccineTypesFromQuery.includes("rsv") ||
    vaccineTypesFromQuery.includes("covid-19&rsv");

  const hasCovid = productsAtLocation.some((id) => id.includes("covid") || id.includes("nextgen"));
  const hasRsv = productsAtLocation.some((id) => id.includes("rsv"));

  const covidOption = productOptions.find((opt) => opt.name.includes("covid"));
  const rsvOption = productOptions.find((opt) => opt.name.includes("rsv"));

  const items = [];
  if (showCovid && covidOption) {
    items.push(
      <Box
        as="li"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "spacing-3",
          mb: "spacing-2"
        }}
      >
        <Icon
          icon={hasCovid ? "check-circle" : "x-circle"}
          color={hasCovid ? "color-system-green-600" : "color-system-red-800"}
          size="xs"
        />
        <Text
          variant={hasCovid ? "body-2-medium" : "body-2-regular"}
          sx={{ color: hasCovid ? "text-default" : "text-incidental" }}
        >
          {covidOption.filterKey}
        </Text>
      </Box>
    );
  }
  if (showRsv && rsvOption) {
    items.push(
      <Box
        as="li"
        sx={{ display: "flex", alignItems: "center", gap: "spacing-3", mb: "spacing-2" }}
      >
        <Icon
          icon={hasRsv ? "check-circle" : "x-circle"}
          color={hasRsv ? "color-system-green-600" : "color-system-red-800"}
          size="xs"
        />
        <Text
          variant={hasRsv ? "body-2-medium" : "body-2-regular"}
          sx={{ color: hasRsv ? "text-default" : "text-incidental" }}
        >
          {rsvOption.filterKey}
        </Text>
      </Box>
    );
  }

  if (items.length === 0) {
    return null;
  }
  return (
    <Box sx={{ my: "spacing-3" }}>
      <Box as="ul" sx={{ listStyle: "none" }}>
        {items}
      </Box>
    </Box>
  );
};

const getDistanceInMilesAndKm = (distanceInMeters: number) => {
  const distanceInMiles = distanceInMeters * 0.000621371;
  const distanceInKilometers = distanceInMeters / 1000;
  return { distanceInMiles, distanceInKilometers };
};

export type ScrollToListItem = (target: HTMLElement | number) => void;

export interface FinderListProps {
  defaultOpen?: boolean;
  distanceTemplate: string;
  listTitle?: string;
  loadMoreTemplate: FinderBlockProps["loadMoreTemplate"];
  locations: FinderLocation[];
  scheduleNowText?: string;
  productOptions: FinderCheckboxProps[];
}

export const FinderList: React.FC<FinderListProps> = ({
  defaultOpen,
  distanceTemplate,
  loadMoreTemplate,
  locations,
  scheduleNowText = "Schedule Now",
  productOptions
}) => {
  const { locationSelected, locationSelectedSet, searching, searchId } = useFinder();
  const locationRefs = React.useRef<(HTMLElement | null)[]>([]);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const { query } = useRouter();
  const { language } = useExperience();

  const scrollToListItem = React.useCallback((target: HTMLElement | number) => {
    const listOffsetTop = listRef.current?.offsetTop || 0;
    const locationOffsetTop = typeof target === "number" ? target : target.offsetTop || 0;
    listRef.current?.scrollTo({
      top: locationOffsetTop - listOffsetTop,
      behavior: "smooth"
    });
  }, []);

  // When selected marker changes, run scrolling logic if possible.
  React.useEffect(() => {
    const refToScrollTo = locationRefs.current.find((refElement) => {
      // find the ref with the same name & address as selected location - there can be multiple "walgreens" so need to check address + name combo
      const customerName = locationSelected?.customer.name.toLowerCase();
      const streetAddress = locationSelected?.location.streetAddress.toLowerCase();
      if (!customerName || !streetAddress) {
        return false;
      }
      return (
        refElement?.textContent?.toLowerCase().includes(customerName) &&
        refElement?.textContent.toLowerCase().includes(streetAddress)
      );
    });
    if (refToScrollTo) {
      scrollToListItem(refToScrollTo);
    }
  }, [locationSelected, scrollToListItem]);

  const renderChildren = (location: FinderLocation) => {
    const { distanceInMiles, distanceInKilometers } = getDistanceInMilesAndKm(
      location.location.distance
    );
    const children = [];
    if (location.products && productOptions.length > 1) {
      children.push(
        <ProductBanner
          key="product-availability"
          productOptions={productOptions}
          productsAtLocation={location.products}
        />
      );
    }
    if (location.customer.website) {
      children.push(
        <Box>
          <Button
            variant="outline"
            data-track="schedule"
            data-track-miles={distanceInMiles}
            data-track-kilometers={distanceInKilometers}
            icon="arrow-right"
            iconPosition="end"
            href={customerWebsite(location.customer, language)}
          >
            {scheduleNowText}
          </Button>
        </Box>
      );
    }
    return children.length > 0 ? children : null;
  };

  return (
    <Box
      // explicilty re-rendering loadMoreButton on search results change - this resets its state and refs for scrolling behavior.
      key={searchId}
      sx={{
        background: "white",
        borderRadius: ["initial", null, "radius-2"],
        display: !!Object.keys(query).length && !searching && locations.length ? "flex" : "none",
        flexDirection: "column",
        maxHeight: "100%",
        flexShrink: "spacing-10",
        pointerEvents: "all",
        width: "100%",
        alignItems: "start",
        overflow: "hidden",
        paddingTop: "spacing-4",
        paddingBottom: "spacing-2",
        paddingRight: "spacing-2"
      }}
    >
      <Box
        ref={listRef}
        sx={{
          width: "100%",
          maxHeight: "100%",
          background: "white",
          overflow: "auto",
          borderRadius: "radius-2",
          // WebKit scrollbar styling:
          // @ts-ignore
          "::-webkit-scrollbar": {
            width: "8px"
          },
          "::-webkit-scrollbar-track": {
            background: "transparent"
          },
          "::-webkit-scrollbar-track:active": {
            background: "transparent"
          },
          "::-webkit-scrollbar-thumb": {
            backgroundColor: "#E0E0E0",
            borderRadius: "radius-4"
          }
        }}
      >
        {locations.map((location: FinderLocation, index: number) => (
          <Box
            key={location.customer.id}
            ref={(el) => (locationRefs.current[index] = el)}
            sx={{
              // @ts-ignore
              backgroundColor: locationSelected === location ? "#F5F5F6" : "white",
              borderBottom: "1px solid",
              borderColor: "stroke-neutral-secondary",
              transition: "transition-ease-out-fast",
              p: 3,
              pl: 6,
              ":last-child": {
                borderBottom: "none"
              },
              ":hover": {
                // @ts-ignore
                backgroundColor: "#F5F5F6"
              }
            }}
            onClick={() => {
              locationSelectedSet(location);
            }}
          >
            <LocationTitle location={location} distanceTemplate={distanceTemplate} />
            {renderChildren(location)}
          </Box>
        ))}
        <LoadMoreButton
          loadMoreTemplate={loadMoreTemplate}
          inMobileMapView={!!defaultOpen}
          scrollToListItem={scrollToListItem}
        />
      </Box>
    </Box>
  );
};
